import * as React from 'react';
import Cookies from 'js-cookie';
import { AvoCable } from '@/lib/avo-cable';
import { ActionStatus } from 'thunkless';
import { useSelector, useDispatch } from 'react-redux';
import { closeCheckoutProcessing } from '@/redux/app/actions/organization';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';
import arrowLeft from '../../../../assets/images/arrow-left.svg';
import { orgCheckoutLoadingStatusSelector } from '../../../redux/app/selectors/organization';
import { UILoader } from '../ui-elements/UILoader';
import { AddSeats } from './AddSeats';
import { AddBalance } from './AddBalance';
import { UpgradePlan } from './UpgradePlan';
import { CheckoutSummary } from './CheckoutSummary';
import { CheckoutProcessing } from './CheckoutProcessing';
import { AppSecretsContext } from '../secrets/app-secrets';

interface UpgradeFlowProps {
  checkoutType: string;
  handleClearCheckoutType: () => void;
}

export const UpgradeFlow = ({
  checkoutType,
  handleClearCheckoutType,
}: UpgradeFlowProps) => {
  const dispatch = useDispatch();
  const { rememberToken } = React.useContext(AppSecretsContext);
  const subdomain = React.useContext(NavigationPaneRouteParamsContext)?.subdomain;

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showSuccess, setIsShowSuccess] = React.useState(false);
  const [text, setText] = React.useState('');
  const handleProcessing = () => setIsProcessing(true);
  const checkoutLoadingStatus = useSelector(orgCheckoutLoadingStatusSelector);
  const handlePurchaseSuccess = (data) => {
    setText(data.message);
    setIsShowSuccess(true);
  };
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (window.stripe_channel) window.stripe_channel.cleanupSubscriptions();

    window.StripeChannel = AvoCable.subscriptions.create({
      channel: 'V3::StripeChannel',
      remember_token: rememberToken || Cookies.get('remember_token'),
      account_subdomain: subdomain,
    }, {
      connected: () => console.log('stripe channel connected'),
      received: (data) => handlePurchaseSuccess(data),
    });

    return () => {
      if (window.StripeChannel) window.StripeChannel.unsubscribe();
    };
  }, []);

  let checkout;
  switch (checkoutType) {
    case 'addPlanUpgrade':
      checkout = <UpgradePlan checkoutSummaryRef={ref} />;
      break;
    case 'addBalance':
      checkout = <AddBalance />;
      break;
    case 'addSeats':
      checkout = <AddSeats />;
      break;
    default:
      break;
  }

  const handleCloseProcessing = () => {
    setIsProcessing(false);
    dispatch(closeCheckoutProcessing());
  };

  if (!checkoutLoadingStatus || checkoutLoadingStatus === ActionStatus.BUSY) return <UILoader />;

  return (
    <div id="upgrade-flow" className="avo-ui-3">
      {isProcessing && (
        <CheckoutProcessing
          text={text}
          setText={setText}
          showSuccess={showSuccess}
          handleCloseProcessing={handleCloseProcessing}
        />
      )}
      <div className="checkout-container-wrap">
        <div
          tabIndex={0}
          role="button"
          className="flex align-center"
          style={{ marginBottom: '30px', cursor: 'pointer', width: 'fit-content' }}
          onClick={handleClearCheckoutType}
          onKeyPress={handleClearCheckoutType}
        >
          <img
            className="back-to-billing"
            src={arrowLeft}
            alt="arrowLeft"
          />
          <span className="bold-800 fsz-13 uppercase">Back to Billing</span>
        </div>
        <div className="checkout-container">
          {checkout}
          <CheckoutSummary checkoutSummaryRef={ref} handleProcessing={handleProcessing} checkoutType={checkoutType} />
        </div>
      </div>
    </div>
  );
};
